/**
 * The weekly timesheet: an employee reviews the week and hands it in;
 * approvers work through submitted weeks and approve, reject or reopen them.
 */
import { useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Paginator } from "@primereact/ui/paginator";
import { Tag } from "@primereact/ui/tag";
import { Textarea } from "@primereact/ui/textarea";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import {
  DateInput,
  FilterTabs,
  Modal,
  SearchBox,
  toDateValue,
} from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { apiGet, apiPost } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, MutationError } from "../shared.js";
import type { TimesheetData } from "./types.js";

type SheetStatus = TimesheetData["status"];

export function mondayOf(date: string): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() - ((value.getDay() + 6) % 7));
  return toDateValue(value);
}

function shiftDays(date: string, days: number): string {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + days);
  return toDateValue(value);
}

export function hm(minutes: number): string {
  const sign = minutes < 0 ? "−" : "";
  const absolute = Math.abs(minutes);
  return `${sign}${Math.floor(absolute / 60)}:${String(absolute % 60).padStart(2, "0")}`;
}

export function sheetSeverity(status: string) {
  if (status === "approved") return "success" as const;
  if (status === "rejected") return "danger" as const;
  if (status === "submitted") return "info" as const;
  return "secondary" as const;
}

/** Week picker shared by the week and approvals pages (`?week=` in the URL). */
function useWeekParam() {
  const [params, setParams] = useSearchParams();
  const week = mondayOf(params.get("week") ?? toDateValue(new Date()));
  const setWeek = (value: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("week", mondayOf(value));
        return next;
      },
      { replace: true },
    );
  return [week, setWeek] as const;
}

function WeekNavigation({
  week,
  onChange,
}: {
  week: string;
  onChange: (week: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const format = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    day: "numeric",
    month: "short",
  });
  const end = shiftDays(week, 6);
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outlined"
        severity="secondary"
        icon="pi pi-chevron-left"
        aria-label={t("time.previousWeek")}
        onClick={() => onChange(shiftDays(week, -7))}
      />
      <Button
        type="button"
        variant="outlined"
        severity="secondary"
        onClick={() => onChange(toDateValue(new Date()))}
      >
        {t("time.thisWeek")}
      </Button>
      <Button
        type="button"
        variant="outlined"
        severity="secondary"
        icon="pi pi-chevron-right"
        aria-label={t("time.nextWeek")}
        onClick={() => onChange(shiftDays(week, 7))}
      />
      <DateInput
        className="w-40"
        ariaLabel={t("time.goToWeek")}
        value={week}
        onChange={(value) => value && onChange(value)}
      />
      <span className="text-sm font-medium">
        {format.format(new Date(`${week}T12:00:00`))} –{" "}
        {format.format(new Date(`${end}T12:00:00`))}
      </span>
    </div>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div className="rounded-xl border border-app-border bg-app-surface p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-app-muted">
        {label}
      </p>
      <p
        className={cx(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "good" && "text-success",
          tone === "bad" && "text-danger",
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function TimeWeekPage() {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const [week, setWeek] = useWeekParam();
  const query = useQuery({
    queryKey: ["time-week", week],
    queryFn: () =>
      apiGet<{ data: TimesheetData }>("/time/week", { query: { week } }).then(
        (response) => response.data,
      ),
  });
  const sheet = query.data;
  const action = useMutation({
    mutationFn: (kind: "submit" | "withdraw") =>
      apiPost(`/time/timesheets/${sheet!.id}/submit`, {
        version: sheet!.version,
        action: kind,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["time-week"] }),
  });
  const dayFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const editable = sheet?.status === "draft" || sheet?.status === "rejected";

  return (
    <>
      <PageHeader
        title={t("time.weekTitle")}
        subtitle={t("time.weekSubtitle")}
        actions={
          sheet ? (
            <>
              <Tag severity={sheetSeverity(sheet.status)}>
                {t(`time.status.${sheet.status}`)}
              </Tag>
              {editable ? (
                <Button
                  type="button"
                  icon="pi pi-send"
                  disabled={sheet.completenessIssues.length > 0}
                  loading={action.isPending}
                  onClick={() => action.mutate("submit")}
                >
                  {t("time.submitWeek")}
                </Button>
              ) : sheet.status === "submitted" ? (
                <Button
                  type="button"
                  variant="outlined"
                  severity="secondary"
                  icon="pi pi-undo"
                  loading={action.isPending}
                  onClick={() => action.mutate("withdraw")}
                >
                  {t("time.withdrawWeek")}
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      <WeekNavigation week={week} onChange={setWeek} />
      {query.isLoading ? (
        <LoadingPanel />
      ) : query.error || !sheet ? (
        <ErrorPanel error={query.error} />
      ) : (
        <div className="space-y-5">
          <MutationError error={action.error} />
          {sheet.status === "rejected" && sheet.rejectionReason ? (
            <div className="rounded-xl border border-danger-border bg-danger-soft p-4 text-sm text-danger">
              <strong>{t("time.rejectedBecause")}</strong>{" "}
              {sheet.rejectionReason}
            </div>
          ) : null}
          {editable && sheet.completenessIssues.length ? (
            <div className="rounded-xl border border-warning-border bg-warning-soft p-4 text-sm">
              <p className="font-medium text-warning">
                {t("time.beforeSubmitting")}
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {sheet.completenessIssues.map((issue, index) => (
                  <li key={`${issue.code}-${issue.date ?? index}`}>
                    {issue.date
                      ? `${dayFormat.format(new Date(`${issue.date}T12:00:00`))}: `
                      : ""}
                    {t(`time.issue.${issue.code}`, {
                      defaultValue: issue.message,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric
              label={t("time.planned")}
              value={hm(sheet.totals.plannedMinutes)}
            />
            <Metric
              label={t("time.worked")}
              value={hm(
                sheet.totals.netAttendanceMinutes +
                  sheet.totals.paidAbsenceMinutes,
              )}
            />
            <Metric
              label={t("time.overtime")}
              value={hm(sheet.totals.compensationDeltaMinutes)}
              tone={sheet.totals.compensationDeltaMinutes < 0 ? "bad" : "good"}
            />
            <Metric
              label={t("time.vacationLeft")}
              value={`${Math.round((sheet.vacation.remaining / 60) * 10) / 10}h`}
            />
          </div>
          <div className="overflow-x-auto rounded-xl border border-app-border bg-app-surface">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="bg-app-background text-left text-xs uppercase tracking-wide text-app-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">{t("common.date")}</th>
                  <th className="px-4 py-2 text-right font-medium">
                    {t("time.planned")}
                  </th>
                  <th className="px-4 py-2 text-right font-medium">
                    {t("time.worked")}
                  </th>
                  <th className="px-4 py-2 text-right font-medium">
                    {t("time.onProjects")}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    {t("time.absences")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border">
                {sheet.days.map((day) => {
                  const absences = Object.entries(day.absenceMinutes).filter(
                    ([, minutes]) => minutes > 0,
                  );
                  const unallocated =
                    day.netAttendanceMinutes !== day.allocatedMinutes;
                  return (
                    <tr
                      key={day.date}
                      className={cx(
                        day.plannedMinutes === 0 && "text-app-muted",
                      )}
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          className="font-medium hover:text-brand"
                          to={`/time/clock?date=${day.date}`}
                        >
                          {dayFormat.format(new Date(`${day.date}T12:00:00`))}
                        </Link>
                        {day.holiday ? (
                          <span className="ml-2 text-xs text-warning">
                            {day.holiday.name}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {hm(day.plannedMinutes)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {hm(day.netAttendanceMinutes)}
                      </td>
                      <td
                        className={cx(
                          "px-4 py-2.5 text-right tabular-nums",
                          unallocated && "text-warning",
                        )}
                      >
                        {hm(day.allocatedMinutes)}
                      </td>
                      <td className="px-4 py-2.5">
                        {absences.map(([kind, minutes]) => (
                          <span
                            key={kind}
                            className="mr-1 inline-block rounded bg-app-background px-1.5 py-0.5 text-xs"
                          >
                            {t(`time.kind.${kind}`)} {hm(minutes)}
                          </span>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-app-muted">
            {t("time.weekHelp")}{" "}
            <Link className="text-brand underline" to="/time/clock">
              {t("time.clock")}
            </Link>{" "}
            ·{" "}
            <Link className="text-brand underline" to="/time/assignments">
              {t("time.assignments")}
            </Link>
          </p>
        </div>
      )}
    </>
  );
}

interface ApprovalRow {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    jobTitle: string | null;
  };
  timesheet: {
    id: string;
    status: SheetStatus;
    version: number;
    submittedAt: string | null;
    rejectionReason: string | null;
  } | null;
  plannedMinutes: number;
  netAttendanceMinutes: number;
  allocatedMinutes: number;
  days: Array<{
    date: string;
    netAttendance: number;
    allocated: number;
    absences: string[];
    projects: Array<{ number: string; title: string; minutes: number }>;
  }>;
}

const APPROVALS_PAGE_SIZE = 25;

type ApprovalFilter = "submitted" | "approved" | "rejected" | "draft" | "all";

export function TimeApprovalsPage() {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const [week, setWeek] = useWeekParam();
  const [status, setStatus] = useState<ApprovalFilter>("submitted");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ApprovalRow | null>(null);
  const [reason, setReason] = useState("");
  const [page, setPage] = useState(1);
  const scope = `${week}|${status}|${search}`;
  const [pagedScope, setPagedScope] = useState(scope);
  if (pagedScope !== scope) {
    setPagedScope(scope);
    setPage(1);
  }
  const result = useQuery({
    queryKey: ["time-approvals", week, status, search, page],
    queryFn: () =>
      apiGet<{ data: { rows: ApprovalRow[]; total: number } }>(
        "/time/management",
        {
          query: {
            week,
            status,
            search: search || undefined,
            page,
            pageSize: APPROVALS_PAGE_SIZE,
          },
        },
      ).then((response) => response.data),
    placeholderData: (previous) => previous,
  });
  const query = { ...result, data: result.data?.rows };
  const decide = useMutation({
    mutationFn: ({
      row,
      action,
      reason,
    }: {
      row: ApprovalRow;
      action: "approve" | "reject" | "reopen";
      reason?: string;
    }) =>
      apiPost(`/time/timesheets/${row.timesheet!.id}/decision`, {
        version: row.timesheet!.version,
        action,
        ...(reason ? { reason } : {}),
      }),
    onSuccess: async () => {
      setRejecting(null);
      setReason("");
      await client.invalidateQueries({ queryKey: ["time-approvals"] });
      await client.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
  const dayFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    weekday: "short",
    day: "numeric",
  });
  const rows = query.data ?? [];

  return (
    <>
      <PageHeader
        title={t("time.approvalsTitle")}
        subtitle={t("time.approvalsSubtitle")}
      />
      <WeekNavigation week={week} onChange={setWeek} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <FilterTabs
          value={status}
          onChange={setStatus}
          options={(
            ["submitted", "approved", "rejected", "draft", "all"] as const
          ).map((value) => ({
            value,
            label:
              value === "all" ? t("common.all") : t(`time.status.${value}`),
          }))}
        />
        <SearchBox value={search} onChange={setSearch} />
      </div>
      <MutationError error={decide.error} />
      {query.isLoading ? (
        <LoadingPanel />
      ) : query.error ? (
        <ErrorPanel error={query.error} />
      ) : !rows.length ? (
        <p className="rounded-xl border border-dashed border-app-border p-8 text-center text-sm text-app-muted">
          {t("time.noTimesheets")}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => {
            const sheet = row.timesheet;
            const expanded = open === row.user.id;
            const delta = row.netAttendanceMinutes - row.plannedMinutes;
            return (
              <li
                key={row.user.id}
                className="rounded-xl border border-app-border bg-app-surface"
              >
                <div className="flex flex-wrap items-center gap-3 p-4">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : row.user.id)}
                  >
                    <i
                      className={cx(
                        "pi text-xs text-app-muted",
                        expanded ? "pi-chevron-down" : "pi-chevron-right",
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block truncate font-medium">
                        {row.user.firstName} {row.user.lastName}
                      </span>
                      <span className="block truncate text-xs text-app-muted">
                        {row.user.jobTitle ?? row.user.email}
                      </span>
                    </span>
                  </button>
                  <dl className="flex gap-5 text-sm tabular-nums">
                    <div>
                      <dt className="text-xs text-app-muted">
                        {t("time.planned")}
                      </dt>
                      <dd>{hm(row.plannedMinutes)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-app-muted">
                        {t("time.worked")}
                      </dt>
                      <dd>{hm(row.netAttendanceMinutes)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-app-muted">
                        {t("time.difference")}
                      </dt>
                      <dd className={delta < 0 ? "text-danger" : undefined}>
                        {hm(delta)}
                      </dd>
                    </div>
                  </dl>
                  <Tag severity={sheetSeverity(sheet?.status ?? "draft")}>
                    {t(`time.status.${sheet?.status ?? "draft"}`)}
                  </Tag>
                  <div className="flex gap-2">
                    {sheet?.status === "submitted" ? (
                      <>
                        <Button
                          type="button"
                          size="small"
                          icon="pi pi-check"
                          loading={decide.isPending}
                          onClick={() =>
                            decide.mutate({ row, action: "approve" })
                          }
                        >
                          {t("time.approve")}
                        </Button>
                        <Button
                          type="button"
                          size="small"
                          variant="outlined"
                          severity="danger"
                          onClick={() => setRejecting(row)}
                        >
                          {t("time.reject")}
                        </Button>
                      </>
                    ) : sheet?.status === "approved" ? (
                      <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        severity="secondary"
                        loading={decide.isPending}
                        onClick={() => decide.mutate({ row, action: "reopen" })}
                      >
                        {t("time.reopen")}
                      </Button>
                    ) : null}
                  </div>
                </div>
                {expanded ? (
                  <div className="overflow-x-auto border-t border-app-border px-4 py-3">
                    {row.days.length ? (
                      <table className="w-full min-w-[32rem] text-sm">
                        <tbody className="divide-y divide-app-border">
                          {row.days.map((day) => (
                            <tr key={day.date}>
                              <td className="w-24 py-1.5 font-medium">
                                {dayFormat.format(
                                  new Date(`${day.date}T12:00:00`),
                                )}
                              </td>
                              <td className="w-16 py-1.5 text-right tabular-nums">
                                {hm(day.netAttendance)}
                              </td>
                              <td className="py-1.5 pl-4 text-app-muted">
                                {[
                                  ...day.absences.map((kind) =>
                                    t(`time.kind.${kind}`),
                                  ),
                                  ...day.projects.map(
                                    (project) =>
                                      `${project.title} ${hm(project.minutes)}`,
                                  ),
                                ].join(" · ") || "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-sm text-app-muted">
                        {t("time.nothingRecorded")}
                      </p>
                    )}
                    {sheet?.rejectionReason ? (
                      <p className="mt-2 text-sm text-danger">
                        {t("time.rejectedBecause")} {sheet.rejectionReason}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {(result.data?.total ?? 0) > APPROVALS_PAGE_SIZE ? (
        <Paginator.Root
          className="mt-3"
          page={page}
          total={result.data?.total ?? 0}
          itemsPerPage={APPROVALS_PAGE_SIZE}
          onPageChange={(event: { value: number }) => setPage(event.value)}
        />
      ) : null}
      <Modal
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        title={t("time.rejectTitle")}
        footer={
          <>
            <Button
              type="button"
              variant="outlined"
              severity="secondary"
              onClick={() => setRejecting(null)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              severity="danger"
              disabled={!reason.trim()}
              loading={decide.isPending}
              onClick={() =>
                rejecting &&
                decide.mutate({
                  row: rejecting,
                  action: "reject",
                  reason: reason.trim(),
                })
              }
            >
              {t("time.reject")}
            </Button>
          </>
        }
      >
        <Field label={t("time.rejectReason")} required>
          <Textarea
            rows={3}
            className="w-full"
            value={reason}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setReason(event.target.value)
            }
          />
        </Field>
      </Modal>
    </>
  );
}
